import {
  Hidden,
  IconButton,
  Radio,
  Tooltip,
  Drawer,
  Stack,
  Typography,
} from "@mui/material";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import { MenuRounded } from "@mui/icons-material";
import { purple } from "@mui/material/colors";
import React from "react";
import { useLocation } from "react-router-dom";
import FilterContent from "../containers/FilterContent";
import { useConfig } from "../containers/ConfigContext";
import { parseImageUrl } from "../lib/helper";

const drawerWidth = 280;

const DrawerNav = styled("nav")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
  },
}));

const TempDrawer = styled(Drawer)(() => ({
  width: drawerWidth - 40,
  "& .MuiDrawer-paper": {
    width: drawerWidth - 40,
  },
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(5)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(7)} + 1px)`,
  },
});

const PermanentDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "expand",
})<{ expand: boolean }>(({ theme, expand }) => ({
  width: drawerWidth,
  flexShrink: 0,
  ...(expand && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!expand && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
  ...(!expand && {
    overflowX: "hidden",
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9) + 1,
    },
    "& .padding-l-large": {
      paddingLeft: theme.spacing(3),
    },
    "& .title": {
      marginLeft: theme.spacing(3),
    },
    "& .subtitle": {
      display: "none",
    },
  }),
}));

const DrawerButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  marginLeft: theme.spacing(2),
  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
  top: 13,
  position: "absolute",
}));

const PinRadio = styled(Radio)(() => ({
  color: purple[400],
  height: 42,
  "&.Mui-checked": {
    color: purple[600],
  },
}));

export const routeTitle: { [key in string]: string } = {
  "": "Dashboard",
  "/": "Dashboard",
  "/branch": "In-Branch Dashboard",
  "/digital": "Digital Dashboard",
  "/fulfilment": "Fulfilment Dashboard",
  "/customer-care": "Customer Care Dashboard",
  "/text": "Text Analytics",
  "/details-report": "Details Report",
  "/review": "Review Dashboard",
  "/stores": "Manage Stores",
  "/users": "Manage Users",
  "/records": "Manage Records",
  "/complains": "Manage Complains",
};

export default function LeftMenu() {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expand, setExpand] = React.useState(true);
  const [pinned, setPin] = React.useState(true);
  const { localized, loggedUser } = useConfig();
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePinCheck = () => {
    setPin(!pinned);
    pinned && setExpand(false);
  };
  const logo = parseImageUrl(loggedUser?.company?.logo);
  const location = useLocation();

  return (
    <DrawerNav aria-label="mailbox folders">
      <Hidden lgUp implementation="css">
        <TempDrawer
          variant="temporary"
          anchor={theme.direction === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          <FilterContent />
        </TempDrawer>
      </Hidden>
      <Hidden lgUp>
        <DrawerButton sx={{ zIndex: 999 }} onClick={handleDrawerToggle}>
          <MenuRounded />
        </DrawerButton>
      </Hidden>
      <Hidden lgDown implementation="css">
        <PermanentDrawer
          onMouseEnter={() => setExpand(true)}
          onMouseLeave={() => setExpand(pinned)}
          expand={expand}
          variant="permanent"
          open
        >
          <Stack
            direction="row"
            alignItems="center"
            my={1}
            pl={1}
            sx={{ width: drawerWidth }}
            spacing={1}
          >
            <Tooltip title="Pin Menu">
              <PinRadio
                color="default"
                checked={pinned}
                onClick={handlePinCheck}
                value="pinned"
              />
            </Tooltip>
            <Typography variant="h6">
              {localized(
                location.pathname.startsWith("/charts")
                  ? "Chart"
                  : routeTitle[location.pathname]
              )}
            </Typography>
          </Stack>
          <div style={{ display: pinned ? "block" : "none" }}>
            <Stack direction={"row"} justifyContent={"center"}>
              {logo && (
                <img
                  src={logo}
                  alt="logo"
                  style={{ height: 50, borderRadius: 8, objectFit: "contain" }}
                />
              )}
            </Stack>
            <FilterContent />
          </div>
        </PermanentDrawer>
      </Hidden>
    </DrawerNav>
  );
}
